import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Heart,
  ChevronDown,
  ChevronUp,
  Truck,
  Shield,
  Award,
} from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addToCart, toggleWishlist, inWishlist } = useCart();

  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [acc, setAcc] = useState("details");

  // Variation state
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});

  // Load product
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);

        setP(data);
        setImg(0);
        setQty(1);
        setSelectedVariation(null);
        setSelectedOptions({});

        const rel = await api.get("/products", {
          params: {
            category: data.category,
            limit: 5,
          },
        });

        setRelated(
          rel.data.filter((x) => x.id !== data.id).slice(0, 4)
        );

        window.scrollTo(0, 0);
      } catch (error) {
        console.error("Product loading failed:", error);
      }
    })();
  }, [id]);

  // Get variations
  const variations = useMemo(() => {
    return Array.isArray(p?.variations)
      ? p.variations.filter(Boolean)
      : [];
  }, [p]);

  // Automatically detect variation fields
  const variationAttributes = useMemo(() => {
    const fields = [
      "size",
      "metal",
      "color",
      "gemstone",
      "purity",
    ];

    return fields
      .map((field) => {
        const values = [
          ...new Set(
            variations
              .map((variation) => variation?.[field])
              .filter(
                (value) =>
                  value !== null &&
                  value !== undefined &&
                  String(value).trim() !== ""
              )
          ),
        ];

        return {
          field,
          label:
            field.charAt(0).toUpperCase() + field.slice(1),
          values,
        };
      })
      .filter((item) => item.values.length > 0);
  }, [variations]);

  // Find matching variation
  useEffect(() => {
    if (!variations.length) {
      setSelectedVariation(null);
      return;
    }

    const allSelected = variationAttributes.every(
      ({ field }) =>
        selectedOptions[field] !== undefined &&
        selectedOptions[field] !== null &&
        String(selectedOptions[field]).trim() !== ""
    );

    if (!allSelected) {
      setSelectedVariation(null);
      return;
    }

    const match = variations.find((variation) =>
      variationAttributes.every(({ field }) => {
        return (
          String(variation?.[field] ?? "") ===
          String(selectedOptions[field] ?? "")
        );
      })
    );

    setSelectedVariation(match || null);

    if (match?.images?.length) {
      setImg(0);
    }

    if (match?.stock !== undefined) {
      setQty((current) =>
        Math.min(
          Math.max(1, current),
          Math.max(1, Number(match.stock))
        )
      );
    }
  }, [selectedOptions, variations, variationAttributes]);

  // Select option
  const chooseOption = (field, value) => {
    setSelectedOptions((previous) => ({
      ...previous,
      [field]: value,
    }));

    setSelectedVariation(null);
    setImg(0);
    setQty(1);
  };

  // Current product values
  const currentPrice =
    selectedVariation?.price ?? p?.price;

  const currentComparePrice =
    selectedVariation?.compare_at_price ??
    p?.compare_at_price;

  const currentStock =
    selectedVariation?.stock ?? p?.stock ?? 0;

  const currentSku =
    selectedVariation?.sku || p?.sku;

  const currentImages =
    selectedVariation?.images?.length
      ? selectedVariation.images
      : p?.images || [];

  const hasVariations = variations.length > 0;

  const variationRequired =
    hasVariations &&
    variationAttributes.length > 0 &&
    !selectedVariation;

  // Add to cart
  const handleAddToCart = async () => {
    if (variationRequired) {
      alert("Please select your preferred options first.");
      return;
    }

    if (currentStock <= 0) {
      alert("This item is out of stock.");
      return;
    }

    await addToCart(
      p.id,
      qty,
      selectedVariation
        ? {
            variation_id:
              selectedVariation.id ||
              selectedVariation._id,
            variation: selectedVariation,
          }
        : null
    );
  };

  // Buy now
  const buyNow = async () => {
    if (variationRequired) {
      alert("Please select your preferred options first.");
      return;
    }

    if (currentStock <= 0) {
      alert("This item is out of stock.");
      return;
    }

    await addToCart(
      p.id,
      qty,
      selectedVariation
        ? {
            variation_id:
              selectedVariation.id ||
              selectedVariation._id,
            variation: selectedVariation,
          }
        : null
    );

    nav("/checkout");
  };

  if (!p) {
    return (
      <div className="ja-container py-32 text-center ja-serif text-2xl">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="ja-container py-12 md:py-16"
      data-testid="product-detail"
    >
      {/* Breadcrumb */}
      <nav
        className="text-xs tracking-[0.2em] uppercase mb-10"
        style={{ color: "var(--ja-muted)" }}
      >
        <Link to="/">Home</Link>
        {" / "}
        <Link to="/shop">Shop</Link>
        {" / "}
        <Link to={`/shop?category=${p.category}`}>
          {p.category}
        </Link>
        {" / "}
        <span style={{ color: "var(--ja-black)" }}>
          {p.name}
        </span>
      </nav>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Gallery */}
        <div className="md:col-span-7">
          <div
            className="aspect-[4/5] overflow-hidden bg-white"
            data-testid="product-main-image"
          >
            {currentImages.length > 0 ? (
              <img
                src={currentImages[img] || currentImages[0]}
                alt={p.name}
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm">
                No image
              </div>
            )}
          </div>

          {currentImages.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-4">
              {currentImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImg(i)}
                  className={`aspect-square overflow-hidden ${
                    i === img
                      ? "ring-1 ring-black"
                      : "opacity-70"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:col-span-5 md:sticky md:top-28 md:self-start">
          <p
            className="ja-overline"
            style={{ color: "var(--ja-gold)" }}
          >
            {p.category} · SKU {currentSku || "—"}
          </p>

          <h1 className="ja-serif text-4xl md:text-5xl mt-3 tracking-tight">
            {p.name}
          </h1>

          {/* Price */}
          <p className="mt-5 text-2xl">
            {formatPrice(currentPrice, p.currency)}

            {currentComparePrice && (
              <span className="line-through ml-3 opacity-50 text-base">
                {formatPrice(
                  currentComparePrice,
                  p.currency
                )}
              </span>
            )}
          </p>

          {/* Stock */}
          <p
            className="mt-2 text-xs tracking-[0.2em] uppercase"
            style={{
              color:
                currentStock > 0
                  ? "var(--ja-success)"
                  : "var(--ja-error)",
            }}
          >
            {currentStock > 0
              ? `In Stock · ${currentStock} Available`
              : "Out of Stock"}
          </p>

          <p
            className="mt-8 text-sm leading-relaxed"
            style={{ color: "var(--ja-muted)" }}
          >
            {p.description}
          </p>

          {/* Variations */}
          {hasVariations && (
            <div className="mt-10 space-y-7">
              <div className="border-t pt-7">
                <p className="text-xs tracking-[0.2em] uppercase mb-5">
                  Select Options
                </p>

                {variationAttributes.map(
                  ({ field, label, values }) => (
                    <div key={field} className="mb-6">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-xs uppercase tracking-[0.15em]">
                          {label}
                        </span>

                        {selectedOptions[field] && (
                          <span
                            className="text-xs"
                            style={{
                              color: "var(--ja-muted)",
                            }}
                          >
                            {selectedOptions[field]}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {values.map((value) => {
                          const selected =
                            selectedOptions[field] === value;

                          return (
                            <button
                              key={String(value)}
                              type="button"
                              onClick={() =>
                                chooseOption(field, value)
                              }
                              className={`px-5 py-3 text-xs border transition-all ${
                                selected
                                  ? "bg-black text-white border-black"
                                  : "bg-white"
                              }`}
                              style={
                                !selected
                                  ? {
                                      borderColor:
                                        "var(--ja-border)",
                                    }
                                  : undefined
                              }
                            >
                              {value}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )
                )}

                {variationRequired && (
                  <p
                    className="text-xs"
                    style={{
                      color: "var(--ja-muted)",
                    }}
                  >
                    Please select all available options.
                  </p>
                )}

                {hasVariations &&
                  Object.keys(selectedOptions).length > 0 &&
                  !selectedVariation &&
                  !variationRequired && (
                    <p
                      className="text-xs"
                      style={{
                        color: "var(--ja-error)",
                      }}
                    >
                      This combination is not available.
                    </p>
                  )}

                {selectedVariation && (
                  <div
                    className="mt-5 p-4 text-xs"
                    style={{
                      background: "#faf8f3",
                      border:
                        "1px solid var(--ja-border)",
                    }}
                  >
                    <div className="flex justify-between">
                      <span>Selected SKU</span>
                      <strong>
                        {selectedVariation.sku ||
                          currentSku ||
                          "—"}
                      </strong>
                    </div>

                    {selectedVariation.stock !==
                      undefined && (
                      <div className="flex justify-between mt-2">
                        <span>Available</span>
                        <strong>
                          {selectedVariation.stock}
                        </strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mt-10 flex items-center gap-6">
            <div
              className="flex items-center border"
              style={{
                borderColor: "var(--ja-border)",
              }}
            >
              <button
                onClick={() =>
                  setQty(Math.max(1, qty - 1))
                }
                className="w-10 h-12"
                data-testid="qty-decrement"
              >
                −
              </button>

              <span
                className="w-10 text-center"
                data-testid="qty-value"
              >
                {qty}
              </span>

              <button
                onClick={() =>
                  setQty(
                    Math.min(
                      Math.max(0, Number(currentStock)),
                      qty + 1
                    )
                  )
                }
                className="w-10 h-12"
                disabled={currentStock <= qty}
                data-testid="qty-increment"
              >
                +
              </button>
            </div>

            <button
              onClick={() => toggleWishlist(p.id)}
              className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase"
              data-testid="pdp-wishlist"
            >
              <Heart
                size={16}
                className={
                  inWishlist(p.id)
                    ? "fill-current"
                    : ""
                }
                style={{
                  color: inWishlist(p.id)
                    ? "var(--ja-gold)"
                    : "var(--ja-black)",
                }}
              />

              {inWishlist(p.id)
                ? "Wishlisted"
                : "Add to Wishlist"}
            </button>
          </div>

          {/* Buttons */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              onClick={handleAddToCart}
              className="ja-btn-outline"
              data-testid="add-to-cart-button"
              disabled={
                currentStock <= 0 ||
                variationRequired
              }
            >
              {variationRequired
                ? "Select Options"
                : "Add to Bag"}
            </button>

            <button
              onClick={buyNow}
              className="ja-btn"
              data-testid="buy-now-button"
              disabled={
                currentStock <= 0 ||
                variationRequired
              }
            >
              Buy Now
            </button>
          </div>

          {/* Trust */}
          <div
            className="mt-10 grid grid-cols-3 gap-4 text-center text-[10px] tracking-[0.2em] uppercase pt-8 border-t"
            style={{
              borderColor: "var(--ja-border)",
            }}
          >
            <div className="flex flex-col items-center gap-2">
              <Truck size={18} />
              <span>Free Shipping</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Shield size={18} />
              <span>Free Returns</span>
            </div>

            <div className="flex flex-col items-center gap-2">
              <Award size={18} />
              <span>BIS Hallmark</span>
            </div>
          </div>

          {/* Accordions */}
          <div
            className="mt-10 border-t"
            style={{
              borderColor: "var(--ja-border)",
            }}
          >
            {[
              [
                "details",
                "Details",
                (
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    {[
                      [
                        "Metal",
                        selectedVariation?.metal ||
                          p.metal,
                      ],
                      [
                        "Color",
                        selectedVariation?.color ||
                          p.color,
                      ],
                      [
                        "Gemstone",
                        selectedVariation?.gemstone ||
                          p.gemstone,
                      ],
                      [
                        "Purity",
                        selectedVariation?.purity ||
                          p.purity,
                      ],
                      ["Weight", p.weight],
                      ["Hallmark", p.hallmark],
                    ]
                      .filter(([_, v]) => v)
                      .map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt
                            className="text-[11px] tracking-[0.2em] uppercase"
                            style={{
                              color:
                                "var(--ja-muted)",
                            }}
                          >
                            {k}
                          </dt>
                          <dd>{v}</dd>
                        </React.Fragment>
                      ))}
                  </dl>
                ),
              ],
              [
                "care",
                "Care & Storage",
                (
                  <p
                    className="text-sm"
                    style={{
                      color: "var(--ja-muted)",
                    }}
                  >
                    Store in the provided pouch.
                    Avoid contact with perfumes,
                    lotions and chlorinated water.
                    Professional cleaning recommended
                    annually.
                  </p>
                ),
              ],
              [
                "shipping",
                "Shipping & Returns",
                (
                  <p
                    className="text-sm"
                    style={{
                      color: "var(--ja-muted)",
                    }}
                  >
                    Complimentary insured shipping
                    worldwide. 3 day free returns on
                    all unworn pieces with original
                    packaging.
                  </p>
                ),
              ],
            ].map(([k, t, body]) => (
              <div
                key={k}
                className="border-b"
                style={{
                  borderColor: "var(--ja-border)",
                }}
              >
                <button
                  onClick={() =>
                    setAcc(acc === k ? "" : k)
                  }
                  className="w-full flex justify-between py-5 text-xs tracking-[0.2em] uppercase"
                  data-testid={`acc-${k}`}
                >
                  {t}

                  {acc === k ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>

                {acc === k && (
                  <div className="pb-6">{body}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="ja-section">
          <div className="text-center mb-12">
            <p className="ja-overline">
              You May Also Love
            </p>

            <h2 className="ja-serif text-3xl md:text-4xl mt-3 tracking-tight">
              Curated For You
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((r, i) => (
              <ProductCard
                key={r.id}
                p={r}
                idx={i}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
           }
